import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle, Clock, ChevronLeft, ChevronRight, Search, X,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@meno/ui/components/ui/select'
import { cn, formatDate, formatTime } from '@meno/ui/lib/utils'
import { contentRoute } from '@/lib/contentRoutes'
import { activityMetaFor, activityTitle, toneTextClass } from '@/lib/activityTypes'

export type ScreenLog = {
  id: number
  event_type: string
  message?: string
  // Structured event context (content_applied carries the publish-task
  // linkage: task_id / kind / content_id / target_label / actor_name).
  meta?: Record<string, unknown> | null
  created_at: string
}

// Icon / category / tone / headline-template per event_type comes from the
// shared activityTypes registry (also used by the org-wide Task Center) —
// see lib/activityTypes.ts. Picks the most specific name available for this
// row's headline — "<Subject> <Verb-ed> ({{name}})" — falling back to the
// screen's own name (screenName) when the event has no more specific target.
function nameFor(log: ScreenLog, screenName?: string | null): string | null {
  if (log.event_type === 'content_applied' && typeof log.meta?.target_label === 'string') {
    return log.meta.target_label
  }
  if (log.event_type === 'content_retry' && typeof log.meta?.item_name === 'string') {
    return log.meta.item_name
  }
  return screenName ?? null
}

// Localized detail line for an event, composed from its structured meta. The
// row title is already localized (activity.type.<type>, via activityTitle());
// this adds the variable part (counts / names / ip / minutes). Returns null
// when there's nothing to add beyond the title, so the feed stays clean
// instead of echoing the English `message`. Legacy rows written before an
// event carried meta fall through to
// null (title-only). content_error is the one exception — its message is a raw
// device diagnostic, shown verbatim. content_applied is handled inline (it
// links to the content), so it isn't here.
type TFn = ReturnType<typeof useTranslation>['t']
function detailFor(log: ScreenLog, t: TFn): string | null {
  const m = log.meta ?? {}
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null)
  const hasNum = (v: unknown) => typeof v === 'number'
  switch (log.event_type) {
    case 'content_error':
      return log.message || null
    case 'connected':
      return str(m.ip) ? t('screens.event.connectedDetail', { defaultValue: 'from {{ip}}', ip: m.ip as string }) : null
    case 'content_updated':
      return (hasNum(m.menus) || hasNum(m.media))
        ? t('screens.event.contentUpdatedDetail', {
            defaultValue: '{{menus}} menus · {{media}} files',
            menus: (m.menus as number) ?? 0, media: (m.media as number) ?? 0,
          })
        : null
    case 'content_retry':
      return str(m.item_name)
        ? t('screens.event.contentRetryDetail', { defaultValue: 'Retrying {{name}}', name: m.item_name as string })
        : null
    case 'offline_alert':
      return hasNum(m.minutes)
        ? t('screens.event.offlineAlertDetail', { defaultValue: 'Offline for over {{minutes}} min', minutes: m.minutes as number })
        : null
    case 'relinked': {
      const from = str(m.from), to = str(m.to)
      return (from || to)
        ? t('screens.event.relinkedDetail', { defaultValue: 'from {{from}} to {{to}}', from: from ?? '—', to: to ?? '—' })
        : null
    }
    case 'admin_unpaired':
    case 'cache_evicted':
      return str(m.admin_email)
        ? t('screens.event.byAdminDetail', { defaultValue: 'by admin {{email}}', email: m.admin_email as string })
        : null
    default:
      return null
  }
}

// The "something needs attention" events — the Problems-only toggle narrows to
// these so an operator can skip the healthy chatter (connects, content applied)
// and see just what went wrong.
const PROBLEM_EVENTS = new Set(['content_retry', 'content_error', 'offline_alert', 'license_expired'])

// Time-window options for the log. Values are millisecond look-backs; 0 = all.
const WINDOWS: { key: string; ms: number; labelKey: string; fallback: string }[] = [
  { key: 'all', ms: 0,               labelKey: 'screens.windowAll',  fallback: 'All time' },
  { key: '24h', ms: 24 * 3600_000,   labelKey: 'screens.window24h', fallback: 'Last 24h' },
  { key: '7d',  ms: 7 * 24 * 3600_000, labelKey: 'screens.window7d', fallback: 'Last 7 days' },
]

// Oldest timestamp still visible for the chosen window (0 = no lower bound).
// A plain function so the `Date.now()` read isn't a bare impure call in the
// component body — same pattern as formatRelativeSyncTime.
function windowCutoff(key: string): number {
  const ms = WINDOWS.find(w => w.key === key)?.ms ?? 0
  return ms ? Date.now() - ms : 0
}

// Rows shown per page before the footer pager kicks in.
const LOGS_PAGE_SIZE = 8

// Shared activity feed for a single screen. Used by the Screen page's Activity
// Log tab and the screens-list info drawer so both read identically. Pass the
// already-loaded logs (getScreenLogs); filtering by event type and paging both
// happen client-side here.
export default function ScreenActivityLog({
  logs, compact = false, screenName,
}: {
  logs: ScreenLog[]
  compact?: boolean
  /** This screen's name — used as the row headline's target name for events
   *  with no more specific one of their own (e.g. "Screen Connected
   *  (Lobby TV)"). Omit only when the caller genuinely doesn't have it. */
  screenName?: string | null
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [typeFilter, setTypeFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [problemsOnly, setProblemsOnly] = useState(false)
  const [timeWindow, setTimeWindow] = useState('all')
  const [page, setPage] = useState(1)
  const PrevIcon = ChevronLeft
  const NextIcon = ChevronRight

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
        <Clock className="w-8 h-8 opacity-30" />
        <p className="text-sm">{t('screens.activityEmpty', 'No activity recorded yet.')}</p>
      </div>
    )
  }

  // Only offer the event types this screen actually has, in feed order.
  const eventTypes = [...new Set(logs.map(l => l.event_type))]
  const q = search.trim().toLowerCase()
  const cutoff = windowCutoff(timeWindow)

  // Compose the four filters: type dropdown, problems-only, time window, and
  // free-text (message / event / content label / publisher).
  const filtered = logs.filter(l => {
    if (typeFilter !== 'all' && l.event_type !== typeFilter) return false
    if (problemsOnly && !PROBLEM_EVENTS.has(l.event_type)) return false
    if (cutoff && new Date(l.created_at).getTime() < cutoff) return false
    if (q) {
      const hay = [
        l.message ?? '',
        l.event_type,
        typeof l.meta?.target_label === 'string' ? l.meta.target_label : '',
        typeof l.meta?.actor_name === 'string' ? l.meta.actor_name : '',
      ].join(' ').toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })

  // Page is clamped so switching to a narrower filter can't strand the user
  // on an out-of-range page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / LOGS_PAGE_SIZE))
  const curPage    = Math.min(page, totalPages)
  const pageItems  = filtered.slice((curPage - 1) * LOGS_PAGE_SIZE, curPage * LOGS_PAGE_SIZE)

  return (
    <div className="space-y-3">
      {/* Toolbar — search + problems toggle, then type/window filters + count. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder={t('screens.searchActivity', 'Search activity…')}
              className="ps-8 pe-8 h-8 text-xs"
            />
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(''); setPage(1) }}
                aria-label={t('common.clearSearch', 'Clear search')}
                className="absolute end-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          {/* Problems-only — one tap to hide healthy chatter. */}
          <button
            type="button"
            onClick={() => { setProblemsOnly(v => !v); setPage(1) }}
            aria-pressed={problemsOnly}
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-medium transition-colors shrink-0',
              problemsOnly
                ? 'border-destructive/40 bg-destructive/10 text-destructive'
                : 'border-border bg-card text-muted-foreground hover:text-foreground',
            )}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            {t('screens.problemsOnly', 'Problems only')}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1) }}>
            <SelectTrigger className="h-8 w-[160px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('screens.allEvents', 'All events')}</SelectItem>
              {eventTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {/* Name-less variant — this lists event TYPES, not a specific
                      instance, so no "(name)" here. */}
                  {activityTitle(t, type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={timeWindow} onValueChange={v => { setTimeWindow(v); setPage(1) }}>
            <SelectTrigger className="h-8 w-[120px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map(w => (
                <SelectItem key={w.key} value={w.key}>{t(w.labelKey, w.fallback)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-[11px] text-muted-foreground tabular-nums ms-auto">
            {t('screens.eventCount', { defaultValue: '{{count}} events', count: filtered.length })}
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          {t('common.noData', 'No data found')}
        </p>
      ) : (
        <ul className="divide-y divide-border/70">
          {pageItems.map(log => {
            const meta = activityMetaFor(log.event_type)
            const Icon = meta.icon
            // Unified headline — "<Subject> <Verb-ed> (<name>)". content_applied
            // names the content that arrived; content_retry names the item being
            // retried; everything else falls back to this screen's own name.
            const title = activityTitle(t, log.event_type, nameFor(log, screenName))
            const route = log.event_type === 'content_applied' && log.meta
              ? contentRoute(log.meta.kind, log.meta.content_id)
              : null
            // content_applied's detail line is just "by <publisher>" — the name
            // itself already moved into the title above (and the title links to
            // it when a route resolves, same convention as the Task Center).
            const detail = log.event_type === 'content_applied'
              ? (typeof log.meta?.actor_name === 'string' && log.meta.actor_name
                  ? t('tasks.byActor', { defaultValue: 'by {{name}}', name: log.meta.actor_name })
                  : null)
              : detailFor(log, t)
            return (
              <li
                key={log.id}
                className={`flex items-start gap-3 px-2 transition-colors hover:bg-muted/25 ${compact ? 'py-2' : 'py-3'}`}
              >
                <span className={cn('mt-0.5 shrink-0', toneTextClass(meta.tone))}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  {route ? (
                    <button
                      type="button"
                      onClick={() => navigate(route)}
                      className="text-sm font-medium leading-snug text-start hover:text-primary hover:underline transition-colors"
                    >
                      {title}
                    </button>
                  ) : (
                    <p className="text-sm font-medium leading-snug">{title}</p>
                  )}
                  {detail && (
                    <p className="text-xs text-muted-foreground truncate" title={detail}>{detail}</p>
                  )}
                </div>
                <time className="text-[11px] text-muted-foreground shrink-0 tabular-nums" dateTime={log.created_at}>
                  {formatDate(log.created_at, i18n.language)} · {formatTime(log.created_at, i18n.language)}
                </time>
              </li>
            )
          })}
        </ul>
      )}

      {/* Pager — only once the filtered feed spills past one page. */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-1">
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {(curPage - 1) * LOGS_PAGE_SIZE + 1}–{Math.min(curPage * LOGS_PAGE_SIZE, filtered.length)} {t('common.of', 'of')} {filtered.length}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={curPage <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              aria-label={t('common.previous', 'Previous')}
            >
              <PrevIcon className="w-4 h-4" />
            </Button>
            <Button
              variant="outline" size="sm" className="h-7 w-7 p-0"
              disabled={curPage >= totalPages}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              aria-label={t('common.next', 'Next')}
            >
              <NextIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
